import { useToast } from '@chakra-ui/react';
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import {
  type CompanyLogoRequest,
  type CompanyLogoUrlResponse,
  CompanyService,
  type CompanyShareRequest,
} from '@/services/api';

const companyLogoKeys = {
  getCompanyLogo: () => ['getCompanyLogo'] as const,
};

export const useGetCompanyLogo = (
  options?: Partial<UseQueryOptions<CompanyLogoUrlResponse, AxiosError>>
) => {
  return useQuery<CompanyLogoUrlResponse, AxiosError>({
    queryKey: companyLogoKeys.getCompanyLogo(),
    queryFn: () => CompanyService.getCompanyLogo(),
    ...options,
  });
};

export const usePutCompanyLogoMutation = (
  options?: UseMutationOptions<unknown, AxiosError, CompanyLogoRequest>
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['components']);

  return useMutation<unknown, AxiosError, CompanyLogoRequest>({
    mutationFn: async (body) => {
      return CompanyService.putCompanyLogo({
        body,
      });
    },
    ...options,
    async onSuccess(data, variables, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: companyLogoKeys.getCompanyLogo(),
        }),
      ]);

      toast({
        status: 'success',
        title: t('components:emailSettings.companyLogoUploadSuccess'),
      });

      options?.onSuccess?.(data, variables, context);
    },
    onError(error) {
      const e = error?.response?.data as { errors: { LogoUrl: Array<string> } };

      toast({
        status: 'error',
        title: e?.errors?.LogoUrl?.[0],
      });
    },
  });
};

export const useDeleteCompanyLogoMutation = (
  options?: UseMutationOptions<void, AxiosError, void>
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['components']);

  return useMutation<void, AxiosError, void>({
    mutationFn: async () => {
      return CompanyService.deleteCompanyLogo();
    },
    ...options,
    async onSuccess(data, variables, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: companyLogoKeys.getCompanyLogo(),
        }),
      ]);

      toast({
        status: 'success',
        title: t('components:emailSettings.companyLogoDeleteSuccess'),
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

type ShareCompanyRequest = {
  interviewId: string;
  body?: CompanyShareRequest;
};

export const useShareCompanyMutation = (
  options?: UseMutationOptions<void, AxiosError, ShareCompanyRequest>
) => {
  return useMutation<void, AxiosError, ShareCompanyRequest>({
    mutationFn: async (body) => {
      return CompanyService.postAllCompanyInterviewShare({
        interviewId: body.interviewId,
        body: body.body,
      });
    },
    ...options,
  });
};
